/**
 * Screen 1: settling where each field of this table reads from.
 *
 * A foreign file's columns are not our field names, and the machine's attempt
 * at matching them is evidence of varying quality. The screen's whole job is to
 * let a reader tell the qualities apart, because only some of them are worth
 * their attention: what was matched exactly needs no thought, what was
 * remembered needs confirming at a glance, what was guessed needs looking at,
 * and what was not matched at all needs a decision.
 *
 * THE DIRECTION is the user's ruling of 2026-08-31: this table's fields on the
 * left, the file on the right, an arrow between them showing which way the
 * data moves. One row per field of ours; the dropdown on the right chooses its
 * source - a column of the file, the table's default, one typed value for
 * every row, or nothing. The separate "missing fields" section stopped
 * existing when the rows flipped: a field with no column is not a different
 * kind of row, it is a row whose source is not a column.
 *
 * Everything entity-specific here arrives in the schema. Nothing on this screen
 * knows whether it is looking at Customers or Leads.
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';

import {
  Mapping,
  TransferSchema,
} from '../../services/table-import-export.service';
import {
  FieldRow,
  FieldSource,
  MappingRow,
  UnusedColumn,
  fieldRows,
  mappingRows,
  readsAColumn,
  requiredWithoutColumn,
  unusedColumns,
} from './mapping-rows';
import { UnusedGroup, groupUnused } from './unused-groups';

export { MappingRow, NO_COLUMN } from './mapping-rows';

@Component({
  selector: 'app-import-mapping-step',
  templateUrl: './import-mapping-step.component.html',
  styleUrls: ['./import-mapping-step.component.css'],
})
export class ImportMappingStepComponent {
  // Setters rather than ngOnChanges: the view is a function of these and
  // nothing else, and ngOnChanges only fires for a template binding - a
  // component assigned to directly, which is how it is driven under test,
  // would have kept the previous file's fields on screen.
  @Input({ required: true })
  set mapping(value: Mapping) { this.mappingValue = value; this.settle(); }
  get mapping(): Mapping { return this.mappingValue; }
  private mappingValue!: Mapping;
  @Input({ required: true })
  set schema(value: TransferSchema) { this.schemaValue = value; this.settle(); }
  get schema(): TransferSchema { return this.schemaValue; }
  private schemaValue!: TransferSchema;
  /**
   * Field to column - the WHOLE agreed mapping once anything has been touched,
   * not a list of differences. A set of overrides cannot say "this field has
   * no column": leaving it out is indistinguishable from not having an opinion,
   * so clearing a column that was matched would silently keep the match.
   */
  @Input()
  set choice(value: Record<string, string>) { this.choiceValue = value; this.settle(); }
  get choice(): Record<string, string> { return this.choiceValue; }
  private choiceValue: Record<string, string> = {};
  @Output() choiceChange = new EventEmitter<Record<string, string>>();

  /**
   * A value for a field no column feeds, on every row. Owned by the dialog
   * like `choice` is, because it is half of the same answer and the two are
   * posted together.
   */
  @Input()
  set constants(value: Record<string, string>) {
    this.constantsValue = value; this.settle();
  }
  get constants(): Record<string, string> { return this.constantsValue; }
  private constantsValue: Record<string, string> = {};
  @Output() constantsChange = new EventEmitter<Record<string, string>>();

  @Input() remember = false;
  @Output() rememberChange = new EventEmitter<boolean>();

  /** What the last Save or Load had to say. Owned by the dialog, which makes
   *  the calls; this screen only shows it. */
  @Input() notice: string | null = null;
  /** Whether a picker is open or a file is being read. */
  @Input() busy = false;

  /**
   * Ask for the mapping to be written to a file, or read back from one.
   *
   * Void, and the screen holds nothing about either: the file dialog is the
   * name box now, and where the file goes is the person's answer rather than
   * ours. There used to be a name box and a list of what the account had
   * kept, which is what saving somewhere invisible costs a screen.
   */
  @Output() save = new EventEmitter<void>();
  @Output() load = new EventEmitter<void>();

  /**
   * Whether anything has been chosen by hand yet. Owned by the dialog, which
   * needs the same answer to decide whether this screen may be left.
   */
  @Input()
  set settledByHand(value: boolean) {
    this.settledByHandValue = value; this.settle();
  }
  get settledByHand(): boolean { return this.settledByHandValue; }
  private settledByHandValue = false;

  /** The file's columns, for the source dropdowns. */
  get headers(): string[] { return this.mapping.headers; }

  /** ONE ROW PER FIELD OF THIS TABLE. See FieldRow for the shape. */
  fields: FieldRow[] = [];

  /** THE FILE'S COLUMNS NOTHING READS - the other side of the mismatch,
   *  marked in the same colour the unfed fields wear. */
  unused: UnusedColumn[] = [];

  /** The same columns, gathered by what can be done about them. The words
   *  belong to the class; a column carries only its own name and field. */
  unusedGroups: UnusedGroup[] = [];

  /** A required field with no source makes every row in the file fail for the
   *  same reason. Worth saying before the plan is built rather than after. */
  requiredWithoutColumn: MappingRow[] = [];

  /** Answers already given, so a re-read of the file keeps what somebody
   *  typed rather than asking again. */
  private heldAnswers = new Map<string, FieldRow>();

  private rows: MappingRow[] = [];

  get requiredWithoutColumnLabels(): string {
    return this.requiredWithoutColumn.map(row => row.label).join(', ');
  }

  get rememberedFromLastTime(): boolean { return this.mapping.remembered; }

  /** Keyed by our field, which is what a line is now. Without this the loop
   *  rebuilds every select on every pass - the wedged-tab bug the WebApp
   *  README describes, met on this screen twice already. */
  trackByField = (_: number, row: { field: string }): string => row.field;

  trackByHeader = (_: number, column: UnusedColumn): string => column.header;

  trackByKind = (_: number, group: UnusedGroup): string => group.kind;

  /** Whether this row feeds its field from a column - hand-picked or the
   *  system's own. The notes about a pairing hang on this, not on which of
   *  the two it is: a guess is still a guess while the row sits on it. */
  readsColumn(row: FieldRow): boolean { return readsAColumn(row); }

  /**
   * The KIND of source, changed. The column stays on the row even while the
   * source is something else: somebody who points a field at a column, tries
   * Blank to see what happens, and comes back should find the column they
   * chose rather than an empty picker and a list to search again.
   *
   * It costs nothing to keep. `update` builds what is posted from the SOURCE,
   * not the header, so a remembered column is invisible to the dialog, the
   * service and the server until it is chosen again.
   */
  chooseSource(field: string, source: FieldSource): void {
    this.update(field, row => ({ ...row, source }));
  }

  chooseColumn(field: string, header: string): void {
    this.update(field, row => ({
      ...row,
      source: 'column',
      header: header === '' ? null : header,
      how: header === '' ? null : 'chosen',
    }));
  }

  setValue(field: string, value: string): void {
    this.update(field, row => ({ ...row, value }));
  }

  setRemember(remember: boolean): void {
    this.remember = remember;
    this.rememberChange.emit(remember);
  }

  /**
   * One answer changed; the two things the dialog posts are rebuilt from all
   * of them. The emitted shapes are unchanged - field -> column and
   * field -> constant - so the dialog, the service and the server see exactly
   * what they saw before the direction flipped.
   */
  private update(field: string, change: (row: FieldRow) => FieldRow): void {
    this.fields = this.fields.map(
      row => row.field === field ? change(row) : row);
    for (const row of this.fields) this.heldAnswers.set(row.field, row);

    const fields: Record<string, string> = {};
    const constants: Record<string, string> = {};
    for (const row of this.fields) {
      // `default` is the system's own column, so it posts one exactly like a
      // hand-picked column does. The difference is which of the two the
      // person is looking at, and that never leaves this screen.
      if (readsAColumn(row)) fields[row.field] = row.header as string;
      if (row.source === 'value' && row.value.trim() !== '')
        constants[row.field] = row.value.trim();
    }
    this.choiceValue = fields;
    this.constantsValue = constants;
    this.settleUnused();
    this.choiceChange.emit(fields);
    this.constantsChange.emit(constants);
  }

  private settle(): void {
    if (!this.schemaValue || !this.mappingValue) return;
    this.rows = mappingRows(
      this.schema, this.mapping, this.choice, this.settledByHand);
    this.requiredWithoutColumn = requiredWithoutColumn(this.rows)
      // A field somebody typed a value for is answered - the dialog's own
      // gate subtracts the same set.
      .filter(row => (this.constantsValue[row.field] ?? '').trim() === '');
    // Typed values arrive back from the dialog as constants; fold them into
    // the held answers so a re-read shows them where they were made.
    for (const [field, value] of Object.entries(this.constantsValue)) {
      const held = this.heldAnswers.get(field);
      if (held) this.heldAnswers.set(field, { ...held, source: 'value', value });
    }
    this.fields = fieldRows(
      this.schema, this.rows, this.choiceValue, this.settledByHand,
      this.heldAnswers, this.mapping);
    this.settleUnused();
  }

  /**
   * The other side of the mismatch, and the classes it falls into.
   *
   * One place because both paths need it: the interactive one, where a column
   * stops being unused the moment a field is pointed at it, and the rebuild,
   * where the file itself may have changed underneath.
   */
  private settleUnused(): void {
    this.unused = unusedColumns(
      this.mapping.headers ?? [], this.fields,
      this.mapping.unwritable ?? [], this.schema);
    this.unusedGroups = groupUnused(this.unused);
  }
}
